import type { CSSProperties } from 'react';

/**
 * Estilos do painel.
 *
 * Deliberadamente sem biblioteca de componentes. O painel é ferramenta de trabalho de uma
 * pessoa — o Junior —, usada no computador, para subir catálogo e moderar. Trazer antd ou
 * Material para isso custaria centenas de quilobytes e uma camada de abstração a mais, sem
 * ninguém ganhando nada. O que importa aqui é a tabela ser legível e o formulário não mentir.
 *
 * As cores vêm de `@venove/core`, as mesmas do site, para o painel não parecer outro produto.
 */
import { color, radius, space } from '@venove/core';

export const ui = {
  pagina: {
    minHeight: '100%',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
  },

  barra: {
    display: 'flex',
    alignItems: 'center',
    gap: space.sp4,
    padding: `${space.sp3}px ${space.sp4}px`,
    borderBottom: `1px solid ${color.line}`,
    position: 'sticky',
    top: 0,
    background: color.backdrop,
    zIndex: 10,
  },

  marca: { fontWeight: 800, letterSpacing: '-0.04em', fontSize: 20, margin: 0 },
  pontoMarca: {
    display: 'inline-block',
    width: '0.22em',
    height: '0.22em',
    borderRadius: '50%',
    background: color.brand,
    marginLeft: '0.04em',
  },

  navLink: {
    color: color.muted,
    textDecoration: 'none',
    fontSize: 14,
    padding: `${space.sp2}px ${space.sp3}px`,
    borderRadius: radius.sm,
  },
  navLinkAtivo: { color: color.text, background: color.surface2 },

  conteudo: { padding: space.sp5, maxWidth: 1100, width: '100%', margin: '0 auto' },

  h1: { fontSize: 24, margin: `0 0 ${space.sp4}px` },
  h2: { fontSize: 17, margin: `${space.sp5}px 0 ${space.sp3}px` },

  tabela: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: {
    padding: `${space.sp2}px ${space.sp3}px`,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    color: color.dim,
    borderBottom: `1px solid ${color.line}`,
    fontWeight: 600,
  },
  td: {
    padding: `${space.sp3}px`,
    fontSize: 14,
    borderBottom: `1px solid ${color.line}`,
    verticalAlign: 'middle',
  },

  campo: {
    width: '100%',
    minHeight: 40,
    borderRadius: radius.sm,
    border: `1px solid ${color.lineStrong}`,
    background: color.surface2,
    color: color.text,
    padding: `${space.sp2}px ${space.sp3}px`,
    fontSize: 14,
    fontFamily: 'inherit',
  },
  rotulo: {
    display: 'block',
    fontSize: 13,
    color: color.muted,
    marginBottom: space.sp1,
  },
  grupo: { marginBottom: space.sp4 },
  ajuda: { fontSize: 12, color: color.dim, marginTop: space.sp1 },
  erro: { color: color.dangerText, fontSize: 13, margin: `${space.sp2}px 0` },

  botao: {
    minHeight: 40,
    padding: `0 ${space.sp4}px`,
    borderRadius: radius.sm,
    border: 0,
    background: color.brand,
    color: color.text,
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
  botaoSecundario: {
    minHeight: 40,
    padding: `0 ${space.sp4}px`,
    borderRadius: radius.sm,
    border: `1px solid ${color.lineStrong}`,
    background: color.surface3,
    color: color.text,
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  botaoPerigo: {
    minHeight: 36,
    padding: `0 ${space.sp3}px`,
    borderRadius: radius.sm,
    border: `1px solid ${color.danger}`,
    background: 'transparent',
    color: color.dangerText,
    fontSize: 13,
    cursor: 'pointer',
  },

  selo: {
    display: 'inline-block',
    padding: `2px ${space.sp2}px`,
    borderRadius: radius.sm,
    fontSize: 12,
    fontWeight: 600,
  },
  seloOk: { background: 'rgba(52,199,123,.16)', color: color.okText },
  seloEspera: { background: 'rgba(255,200,61,.16)', color: color.warn },
  seloNeutro: { background: color.surface2, color: color.muted },

  aviso: {
    background: color.surface1,
    border: `1px solid ${color.line}`,
    borderRadius: radius.md,
    padding: space.sp4,
    fontSize: 13,
    color: color.muted,
    lineHeight: 1.5,
    marginBottom: space.sp4,
  },

  linhaBotoes: { display: 'flex', gap: space.sp3, alignItems: 'center' },
  dim: { color: color.dim },
} satisfies Record<string, CSSProperties>;

export { color, radius, space };
